import { isPlatform } from '@ionic/react';
import { FC, useEffect } from 'react';
import { DeviceValidator, IDeviceInfo } from './DeviceValidator';
import { BasicHeader } from '../components/BasicHeader';
import { createUseStyles } from 'react-jss';
import { IonPage } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { ITrackingContext, MixPanelEventEmitter as MixpanelService } from '../metrics/MixPanelEventEmitter';
import { Href } from '../components/Href';
import { APP_STORE_URLS } from '../config/EnvConfiguration';

const useStyles = createUseStyles({
    body: {
        height: '100%',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'stretch',
        background: 'var(--ion-color-primary)',
        // cover the bottom border of BasicHeader
        marginTop: -1,
        zIndex: 999
    },

    messageBox: {
        background: 'white',
        borderRadius: 8,
        padding: '20px 30px',
        boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-around',
        position: 'relative',
        width: '85%',
        height: '100%',
        maxWidth: 650,
        maxHeight: 500,
        transform: 'translateY(-30px)',
        margin: 'auto'
    },

    title: {
        textAlign: 'center',
        fontWeight: 700,
        marginBottom: 0
    },

    message: {
        maxWidth: 400,
        textAlign: 'center'
    },

    storeButton: {
        maxWidth: 200,
        margin: '20px auto'
    },

    logo: {
        width: '100%',
        height: '100%',
        maxHeight: 120,
        margin: 10,
        backgroundImage: 'url(./assets/images/unsupported/care-map.svg)',
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPositionX: 'center',
    }
});

const getInfoForMixpanel = (deviceInfo: IDeviceInfo): ITrackingContext => {
    const formattedInfo = [
        deviceInfo.vendor || '[No Vendor]',
        deviceInfo.type || '[No Type]',
        deviceInfo.browserName || '[No Browser]',
        deviceInfo.browserVersion || '[No Version]'
    ];
    return {
        'Unsupported Device Info': formattedInfo.join(', '),
        'UA String': deviceInfo.ua
    };
};

export const Unsupported: FC = () => {
    const validator = useInstance(DeviceValidator);
    const { validDevice, deviceInfo } = validator.validate();

    const classes = useStyles();
    const tracking = useInstance(MixpanelService);

    useEffect(() => {
        tracking.trackWithDebounce({
            event: !validDevice ? 'Unsupported Device' : 'Unsupported Browser',
            getContext: () => getInfoForMixpanel(deviceInfo)
        });
    }, [tracking]);  // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <IonPage>
            <BasicHeader />
            <div className={classes.body}>
                <div className={classes.messageBox}>
                    <div className={classes.title}>
                        <h2>Sorry, Innerhive is not supported here</h2>
                    </div>
                    {!isPlatform('ios') && !isPlatform('android') &&
                        <div className={classes.message}>
                            <p>
                                You can access Innerhive on the web using a computer with a recent version of Chrome, Firefox, Safari, or Edge.
                                For the optimal experience, we recommend the latest version of <Href url="https://www.google.com/chrome/dr/download/">Chrome</Href>.
                            </p>
                            <p>
                                Or you can download and install our app on <Href url={APP_STORE_URLS.ios}>iPhones and iPads</Href>,
                                and on <Href url={APP_STORE_URLS.android}>Android phones and tablets</Href>.
                            </p>
                        </div>
                    }
                    {isPlatform('ios') &&
                        <div className={classes.message}>
                            Please download the Innerhive app from the App Store!
                            <div className={classes.storeButton}>
                                <Href url={APP_STORE_URLS.ios}><img src="assets/images/innerhive-plus/app-store-badge.svg" alt="App Store" /></Href>
                            </div>
                        </div>
                    }
                    {isPlatform('android') &&
                        <div className={classes.message}>
                            Please download the Innerhive from Google Play!
                            <div className={classes.storeButton}>
                                <Href url={APP_STORE_URLS.android}><img src="assets/images/innerhive-plus/play-store-badge.svg" alt="Google Play" /></Href>
                            </div>
                        </div>
                    }
                    <div className={classes.logo} />
                </div>
            </div>
        </IonPage>
    );
};
